import { useEffect, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";

import { screenIcon } from "@/lib/nav";

/** パンくずの中間項目（ホームと現在地の間に挟む）。 */
export interface Breadcrumb {
  label: string;
  to: string;
}

/**
 * ブラウザのタブに出す既定の名前（``index.html`` の ``<title>`` と同じ）。
 * 画面を離れたらここへ戻す。
 */
const BASE_TITLE = "BOMIEL - 京和精工";

/**
 * 一覧カード上部のパンくず＋アクション行。
 *
 * ngtg_msc の `_Breadcrumb`（ホーム >> 受注管理）と `PaginatedDataTable2` の
 * header/actions に対応する。文字サイズ（16 / 10 / 20px）と間隔は Flutter 側の
 * 実装値に合わせてある。
 *
 * `parents` を渡すと「ホーム >> 見積管理 >> Q2026-0001」のように中間を挟める。
 *
 * **画面の見分け**もここが受け持つ。見積管理と案件管理は枠（カード＋テーブル）
 * が同じで、違うのは見出しの文字だけだった。ドロワーとホームのタイルで使って
 * いる絵を見出しに添え、ブラウザのタブにも画面名を出す（[[screenIcon]]）。
 * アイコンは現在地のパスから引くので、画面側は何も渡さなくてよい。
 */
export default function PageHeader({
  title,
  parents = [],
  actions,
}: {
  title: string;
  parents?: Breadcrumb[];
  actions?: ReactNode;
}) {
  const { pathname } = useLocation();
  const Icon = screenIcon(pathname);

  // タブを行き来しても、どちらの画面を開いているのかが分かるようにする。
  // 詳細画面では見積番号・工番がそのまま入る（「Q2026-0017 | BOMIEL」）。
  useEffect(() => {
    document.title = `${title} | BOMIEL`;
    return () => {
      document.title = BASE_TITLE;
    };
  }, [title]);

  return (
    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2 px-6 py-3">
      <Link
        to="/"
        className="p-1 text-base text-brand-500 underline underline-offset-2 hover:text-brand-600"
      >
        ホーム
      </Link>
      {parents.map((parent) => (
        <span key={parent.to} className="flex items-center gap-x-2.5">
          <Separator />
          <Link
            to={parent.to}
            className="p-1 text-base text-brand-500 underline underline-offset-2 hover:text-brand-600"
          >
            {parent.label}
          </Link>
        </span>
      ))}
      <Separator />
      {/* 絵と見出しは 1 つの塊として読ませたいので、パンくずの間隔より詰める。 */}
      <span className="flex items-center gap-1.5 text-brand-500">
        {Icon && <Icon size={20} aria-hidden />}
        <h1 className="text-xl font-bold">{title}</h1>
      </span>
      {actions && (
        <div className="ml-auto flex flex-wrap items-center gap-2">{actions}</div>
      )}
    </div>
  );
}

function Separator() {
  return <span className="text-[10px] font-bold text-brand-500">&gt;&gt;</span>;
}
